import { Action } from 'redux';
import { put, select } from 'redux-saga/effects';

import { CheckoutActionTypes, CheckoutActions } from '../';
import { ACTION_CREATOR_NAME, GtmCheckoutSteps, ExponeaCheckoutSteps, analyticsHelper, errorToBasketErrorModel } from 'helpers';
import { AppState, ErrorAction } from 'store';
import { api } from 'repositories/api';
import { BasketDetailsModel } from 'models/Basket';
import { PaymentTypes } from 'models/Payment';
import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import { BasketSagaReturnType } from 'store/sagaHelpers';

export interface PreprocessRedirectToNextStepAction extends Action<CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP> {
    redirectFunc: () => void;
    gtmStep: GtmCheckoutSteps;
    exponeaStep?: ExponeaCheckoutSteps;
}

export interface PreprocessRedirectToNextStepActionStart extends Action<CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP_START> {
}

export interface PreprocessRedirectToNextStepActionSuccess extends Action<CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP_SUCCESS> {
}

export interface PreprocessRedirectToNextStepActionError extends ErrorAction<CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP_ERROR> {
}

export const preprocessRedirectToNextStep: (redirectFunc: () => void, gtmStep: GtmCheckoutSteps, exponeaStep?: ExponeaCheckoutSteps) => PreprocessRedirectToNextStepAction = (redirectFunc: () => void, gtmStep: GtmCheckoutSteps, exponeaStep?: ExponeaCheckoutSteps) => ({
    type: CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP,
    redirectFunc,
    gtmStep,
    exponeaStep

});
preprocessRedirectToNextStep[ACTION_CREATOR_NAME] = 'preprocessRedirectToNextStep';

export type PreprocessRedirectToNextStepFunc = (...args: Parameters<typeof preprocessRedirectToNextStep>) => void;

export function* preprocessRedirectToNextStepSaga({ redirectFunc, gtmStep, exponeaStep }: PreprocessRedirectToNextStepAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP_START
    });

    try {
        let state: AppState = yield select();
        const basket = state.basketState.basket;

        if (basket && state.paymentState.paymentDetails.paymentType !== PaymentTypes.None) {
            yield sendAnalytics(gtmStep, exponeaStep, basket, state.paymentState.paymentDetails.paymentType, basket.user.uid);
        } else {
            throw new BasketErrorModel(
                DEFAULT_ERROR_TEXT,
                `${basket ? '' : 'Basket does not exist'};
                ${state.paymentState?.paymentDetails?.paymentType ? '' : 'Payment details does not exist'};
                ${state.paymentState?.paymentDetails?.paymentType === PaymentTypes.None ? 'Payment details is None' : ''}`,
                'preprocessRedirectToNextStepSaga'
            );
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP_ERROR,
            error: errorToBasketErrorModel(error, 'preprocessRedirectToNextStepSaga')
        });

        return false;
    } finally {
        redirectFunc();
    }
}

function* sendAnalytics(gtmStep: GtmCheckoutSteps,
    exponeaStep: ExponeaCheckoutSteps | undefined,
    basket: BasketDetailsModel,
    paymentType: PaymentTypes,
    userId: string
): Generator<any, void, any> {
    yield analyticsHelper.withLoggingGenerator(function* () {
        if (exponeaStep) {
            if (exponeaStep === ExponeaCheckoutSteps.DeliveryDate) {
                let state: AppState = yield select();
                api.exponea.checkoutStageWithDeliveryDate(exponeaStep, basket, paymentType, state.checkoutState.selectedQuickestDeliveryDate, state.checkoutState.deliverNotBefore!);
            } else {
                api.exponea.checkoutStage(exponeaStep, basket, paymentType);
            }
        }

        if (gtmStep === GtmCheckoutSteps.Basket
            || gtmStep === GtmCheckoutSteps.DeliveryDate
            || gtmStep === GtmCheckoutSteps.PaymentOrDpn
            || gtmStep === GtmCheckoutSteps.DeliveryDetails
            || gtmStep === GtmCheckoutSteps.PaymentError
            || gtmStep === GtmCheckoutSteps.OrderProcessing
        ) {
            api.google.checkoutStep(gtmStep, basket.guid, basket.entries, basket.leadTime, paymentType, userId);
        } else if (gtmStep === GtmCheckoutSteps.FinanceFormStepOne
            || gtmStep === GtmCheckoutSteps.FinanceFormStepTwo
            || gtmStep === GtmCheckoutSteps.FinanceFormStepThree
            || gtmStep === GtmCheckoutSteps.FinanceFormStepFour) {
            api.google.financeStep(gtmStep, basket.guid, basket.entries, basket.leadTime, paymentType, userId);
        }
    }, 'preprocessRedirectToNextStepSaga');
}